import { useState } from 'react';
import { Link } from 'react-router';
import type { Project } from '@jabberr/core';
import { FolderIcon, UserIcon } from '../components/icons.js';
import { useSession } from '../state/session.js';
import '../styles/home.css';

function ProjectCard({ project }: { project: Project }) {
  const description = project.description?.trim() || 'Open this project to continue a conversation or start a new one.';
  return (
    <li>
      <Link to={`/p/${project.id}`} className="home-project-card" aria-label={`Open ${project.name}`} aria-describedby={`home-purpose-${project.id}`}>
        <span className="home-project-kind"><FolderIcon />{project.kind === 'private' ? 'Private project' : 'Project'}</span>
        <h3>{project.name}</h3>
        <p id={`home-purpose-${project.id}`} className="home-project-purpose">{description}</p>
        <span className="home-project-open">Open project <span aria-hidden="true">→</span></span>
      </Link>
    </li>
  );
}

export function ProjectsPage() {
  const { projects, loading, refreshProjects } = useSession();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'active' | 'archived'>('active');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const personal = projects.filter((project) => project.kind === 'personal');
  const work = projects.filter((project) => project.kind !== 'personal' && !project.isIdea);
  const filter = query.trim().toLocaleLowerCase();
  const matches = work.filter((project) => project.status === status &&
    `${project.name} ${project.description ?? ''}`.toLocaleLowerCase().includes(filter));

  const refresh = async () => {
    setRefreshing(true);
    setRefreshError(false);
    try { await refreshProjects(); }
    catch { setRefreshError(true); }
    finally { setRefreshing(false); }
  };

  return (
    <div className="page home-page">
      <header className="home-heading">
        <div>
          <p className="home-eyebrow">Jabberr</p>
          <h1>Your projects</h1>
          <p className="home-intro">Choose a project to continue a conversation or start a new one.</p>
        </div>
        {!loading && <button type="button" className="btn" disabled={refreshing} onClick={() => void refresh()}>{refreshing ? 'Refreshing…' : 'Refresh projects'}</button>}
      </header>

      {refreshError && <p role="alert" className="error-box">Projects could not be refreshed. Try again.</p>}

      {loading ? <p role="status" className="home-empty">Loading your projects…</p> : (
        <>
          {personal.length > 0 && (
            <section className="home-personal" aria-label="My space">
              {personal.map((project) => (
                <Link key={project.id} to={`/p/${project.id}`} className="home-personal-link" aria-label={`Open My space${personal.length > 1 ? `: ${project.name}` : ''}`}>
                  <span className="home-personal-icon" aria-hidden="true"><UserIcon /></span>
                  <span className="home-personal-copy"><strong>My space{project.status === 'archived' ? ' · Archived' : ''}</strong><span>A place for your own conversations.</span></span>
                  <span className="home-personal-arrow" aria-hidden="true">→</span>
                </Link>
              ))}
            </section>
          )}

          {work.length > 0 ? (
            <section aria-labelledby="home-projects-heading">
              <div className="home-projects-heading">
                <h2 id="home-projects-heading">Projects</h2>
                <label className="home-search">Search projects<input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name or description" /></label>
              </div>
              <div className="home-project-filters" role="group" aria-label="Project status">
                {(['active', 'archived'] as const).map((value) => (
                  <button key={value} type="button" aria-pressed={status === value} onClick={() => setStatus(value)}>
                    {value === 'active' ? 'Active' : 'Archived'} <span>{work.filter((project) => project.status === value).length}</span>
                  </button>
                ))}
              </div>
              <p className="home-result-count" role="status">{matches.length} {status} {matches.length === 1 ? 'project' : 'projects'}{filter ? ' match your search' : ''}</p>
              {matches.length > 0 ? <ul className="home-project-grid">{matches.map((project) => <ProjectCard key={project.id} project={project} />)}</ul> : (
                <div className="home-empty">
                  <h3>{filter ? 'No matching projects' : `No ${status} projects`}</h3>
                  <p>{filter ? 'Try a different name or description, or check the other project status.' : status === 'active' ? 'Your archived projects are still available in Archived.' : 'Archived projects will appear here.'}</p>
                  {filter && <button type="button" className="btn" onClick={() => setQuery('')}>Clear search</button>}
                </div>
              )}
            </section>
          ) : (
            <section className="home-empty" aria-labelledby="home-empty-heading">
              <h2 id="home-empty-heading">No projects yet</h2>
              <p>{personal.length > 0 ? 'You can use My space while you wait for a project.' : 'Projects you have access to will appear here.'} If you are expecting a shared project, ask its owner to confirm your access, then refresh this page.</p>
            </section>
          )}
        </>
      )}
    </div>
  );
}
